import { ReactNode, useEffect, useRef, useState } from 'react';
import { Collapse, CollapseProps, theme } from 'antd';
import { selectActiveScanRequestId } from '../../../modules/scan-requests/store/scan-requests-slice';
import { selectActiveSignRequestId } from '../../../modules/sign-requests/store/sign-requests-slice';
import { useAppSelector } from '../../../store/hooks';
import { ArrowDownIcon } from '../../icons/arrow-down-icon';
import { ArrowUpIcon } from '../../icons/arrow-up-icon';
import styles from './collapsable-preview.module.css';

interface Props {
  title: string;
  index: string;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  defaultActiveKey?: CollapseProps['defaultActiveKey'];
}

const CollapsablePreview = ({ title, index, children, className, icon, defaultActiveKey }: Props): JSX.Element => {
  const [activeKey, setActiveKey] = useState(defaultActiveKey);
  const activeSignRequestId = useAppSelector(selectActiveSignRequestId);
  const activeScanRequestId = useAppSelector(selectActiveScanRequestId);
  const panelRef = useRef<HTMLDivElement>(null);
  const { token } = theme.useToken();

  useEffect(() => {
    if (panelRef.current && (index === activeSignRequestId || index === activeScanRequestId)) {
      setTimeout(
        () => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        Math.round(parseFloat(token.motionDurationSlow) * 1.5 * 1000),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScanRequestId, activeSignRequestId, index]);

  useEffect(() => {
    setActiveKey(defaultActiveKey);
  }, [defaultActiveKey]);

  return (
    <Collapse
      expandIcon={panelProps => (panelProps.isActive ? <ArrowUpIcon /> : <ArrowDownIcon />)}
      expandIconPlacement="end"
      activeKey={activeKey}
      className={className}
      onChange={setActiveKey}
    >
      <Collapse.Panel
        ref={panelRef}
        className={styles.panelContainer}
        header={
          <>
            {icon && <span className={styles.collapseTitleIcon}>{icon}</span>}
            <span className={styles.collapseTitle}>{title}</span>
          </>
        }
        key={index}
      >
        {children}
      </Collapse.Panel>
    </Collapse>
  );
};

export default CollapsablePreview;
